import { useEffect } from 'react'
import { useAppDispatch } from '@/store/hooks'
import { edgeDataApi } from '@/services/api/data-api'
import { PRICE_ALERTS_LIST_TAG, edgePriceAlertsApi } from '@/services/api/price-alerts-api'
import { getRealtimeClient } from '@/services/realtime/realtime-service'
import { useNotificationStore } from '@/stores/notification-store'
import { DEFAULT_AUDIO } from '@/features/scanner/alerts/audio/sound-catalog'
import type { ISoundPlayer } from '@/features/scanner/alerts/audio/sound-player'
import { applyPriceAlertChange, startPriceAlertNotifications } from './price-alert-notifications'
import { usePriceAlertSoundStore } from './price-alert-sound-store'

/**
 * App-wide price-alert toasts + list refresh, over the tab's realtime socket.
 * A fired alert also pings `sound` (the app's shared, already-unlocked player)
 * unless the user muted it in the panel footer.
 */
export function usePriceAlertNotifications(sound: ISoundPlayer): void {
  const dispatch = useAppDispatch()
  useEffect(
    () =>
      startPriceAlertNotifications({
        client: getRealtimeClient(),
        invalidate: () => dispatch(edgeDataApi.util.invalidateTags([PRICE_ALERTS_LIST_TAG])),
        // Spec C5: the line leaves (fired) or moves (adjusted) before the refetch lands.
        patch: (change) =>
          dispatch(
            edgePriceAlertsApi.util.updateQueryData('listPriceAlerts', 'active', (draft) => {
              applyPriceAlertChange(draft.alerts, change)
            }),
          ),
        notify: {
          showSuccess: (m, o) => useNotificationStore.getState().showSuccess(m, o),
          showInfo: (m, o) => useNotificationStore.getState().showInfo(m, o),
        },
        onFired: () => {
          if (usePriceAlertSoundStore.getState().enabled) {
            sound.play(DEFAULT_AUDIO).catch(() => {})
          }
        },
      }),
    [dispatch, sound],
  )
}
